import {
  ApplicationConfig,
  importProvidersFrom,
  inject,
  isDevMode,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { routes } from './app.routes';
import { AppInterceptor } from '@services/app.interceptor';
import { provideToastr, ToastrService } from 'ngx-toastr';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { provideTransloco, TranslocoService } from '@jsverse/transloco';
import {
  BrowserAnimationsModule,
  provideAnimations,
} from '@angular/platform-browser/animations';
import { CustomToasterService, TranslocoHttpLoader } from '@core-utils';
import { SwitchLanguageService, ThemeModeService } from '@ui-kit';
import { provideCoreDataScope } from '@shared/core-data';
import { OAuthService, provideOAuthClient } from 'angular-oauth2-oidc';
import { LandingAuthService } from '@campaign-landing/shared';
import { authCodeFlowConfig } from '@config/auth.config';

function initializeAuth(oauthService: OAuthService): Promise<void> {
  oauthService.configure(authCodeFlowConfig);
  oauthService.setupAutomaticSilentRefresh();

  return oauthService
    .loadDiscoveryDocumentAndTryLogin()
    .then(() => undefined)
    .catch(() => undefined);
}

export const appConfig: ApplicationConfig = {
  providers: [
    OAuthService,
    provideOAuthClient(),
    LandingAuthService,
    provideAppInitializer(() => initializeAuth(inject(OAuthService))),
    ThemeModeService,
    SwitchLanguageService,
    provideAnimations(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideToastr(),
    provideHttpClient(withInterceptors([AppInterceptor])),
    importProvidersFrom([BrowserAnimationsModule]),
    provideEnvironmentNgxMask({
      validation: false,
    }),
    provideTransloco({
      config: {
        availableLangs: ['en', 'fr', 'es'],
        defaultLang: SwitchLanguageService.getStoredLanguage(),
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader,
    }),
    provideCoreDataScope(),
    {
      provide: CustomToasterService,
      useClass: CustomToasterService,
      deps: [ToastrService, TranslocoService],
    },
  ],
};
